import { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { ConfigProvider, App as AntApp, theme as antTheme } from 'antd'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { LoginPage } from './auth/pages/LoginPage'
import { ForbiddenPage } from './auth/pages/ForbiddenPage'
import { ProtectedRoute } from './auth/components/ProtectedRoute'
import { MainLayout } from './shared/components/MainLayout'
import { DashboardPage } from './dashboard/screens/DashboardPage'
import { UsersScreen } from './features/users'
import { MerchantsScreen, MerchantDetailsScreen } from './features/merchants'
import { AdminsScreen } from './features/admins'
import { CampaignListScreen } from './features/referrals/screens/CampaignListScreen'
import { CampaignCreateScreen } from './features/referrals/screens/CampaignCreateScreen'
import { ReferralsDashboardScreen } from './features/referrals/screens/ReferralsDashboardScreen'

import { RFQsScreen } from './features/rfqs'
import { OrdersScreen } from './features/orders'
import { PaymentsScreen } from './features/payments'
import { SettlementsScreen } from './features/settlements'
import { DisputesScreen } from './features/disputes'
import { TrialsScreen } from './features/trials'

import { AnalyticsScreen } from './features/analytics'
import { SettingsScreen } from './features/settings'
import { NotificationsScreen, NotificationTemplatesScreen } from './features/notifications'
import { RolesScreen } from './features/roles'
import { ProfileScreen } from './features/profile'
import { AuditLogsScreen } from './features/audit-logs'
import { ProductsScreen, ProductAttributesScreen } from './features/products'
import { BrandsScreen } from './features/brands'
import { CategoriesScreen } from './features/categories'
import { UnitsScreen } from './features/products/screens/UnitsScreen'
import { CommissionsScreen } from './features/commissions'
import { PromotionsScreen } from './features/promotions'
import { MarketplaceInvoicesScreen, SubscriptionInvoicesScreen } from './features/invoices'
import { SubscriptionsDashboard } from './features/subscriptions'
import { FinancialsOverviewScreen } from './features/financials/pages/FinancialsOverviewScreen'
import { AdjustmentsScreen } from './features/financials/pages/AdjustmentsScreen'
import { MerchantLedgerViewer } from './features/financials/pages/MerchantLedgerViewer'
import { useThemeStore } from './shared/store/theme-store'
import { PlaceholderScreen } from './shared/components/PlaceholderScreen'
import { NotFoundScreen } from './shared/components/NotFoundScreen'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

// Create a client
const queryClient = new QueryClient()

function App() {
  const { primaryColor, isDarkMode } = useThemeStore()

  // Apply theme on mount
  useEffect(() => {
    document.documentElement.style.setProperty('--color-primary', primaryColor)
    if (isDarkMode) {
      document.documentElement.classList.add('dark-mode')
    }
  }, [primaryColor, isDarkMode])

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: primaryColor,
            colorSuccess: '#10b981',
            colorWarning: '#f59e0b',
            colorError: '#ef4444',
            colorInfo: '#3b82f6',
            borderRadius: 8,
          },
          algorithm: isDarkMode ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
        }}
      >
        <AntApp>
          <Sentry.ErrorBoundary
            fallback={({ error, resetError }) => (
              <div style={{ padding: 40, textAlign: 'center' }}>
                <h2>Something went wrong</h2>
                <p style={{ color: '#ef4444' }}>{error instanceof Error ? error.message : 'An unexpected error occurred.'}</p>
                <button
                  onClick={() => resetError()}
                  style={{ padding: '8px 16px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                >
                  Reload Page
                </button>
              </div>
            )}
          >
            <BrowserRouter>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/403" element={<ForbiddenPage />} />

                {/* Protected routes with layout */}
                <Route element={<ProtectedRoute />}>
                <Route element={<MainLayout />}>
                  <Route path="/dashboard" element={<DashboardPage />} />

                  {/* User Management - requires users.view */}
                  <Route path="/users" element={<ProtectedRoute permissions={['users.view']}><UsersScreen /></ProtectedRoute>} />

                  {/* Merchant Management - requires merchants.view */}
                  <Route path="/merchants" element={<ProtectedRoute permissions={['merchants.view']}><MerchantsScreen /></ProtectedRoute>} />
                  <Route path="/merchants/:id" element={<ProtectedRoute permissions={['merchants.view']}><MerchantDetailsScreen /></ProtectedRoute>} />
                  <Route path="/trials" element={<ProtectedRoute permissions={['merchants.view']}><TrialsScreen /></ProtectedRoute>} />

                  {/* Referrals & Campaigns */}
                  <Route path="/campaigns" element={<ProtectedRoute permissions={['merchants.view']}><CampaignListScreen /></ProtectedRoute>} />
                  <Route path="/campaigns/create" element={<ProtectedRoute permissions={['merchants.view']}><CampaignCreateScreen /></ProtectedRoute>} />
                  <Route path="/referral-analytics" element={<ProtectedRoute permissions={['merchants.view']}><ReferralsDashboardScreen /></ProtectedRoute>} />


                  {/* Product Management - requires products.view */}
                  <Route path="/products" element={<ProtectedRoute permissions={['products.view']}><ProductsScreen /></ProtectedRoute>} />
                  <Route path="/products/attributes" element={<ProtectedRoute permissions={['products.view']}><ProductAttributesScreen /></ProtectedRoute>} />
                  <Route path="/brands" element={<ProtectedRoute permissions={['products.view']}><BrandsScreen /></ProtectedRoute>} />
                  <Route path="/categories" element={<ProtectedRoute permissions={['products.view']}><CategoriesScreen /></ProtectedRoute>} />
                  <Route path="/commissions" element={<ProtectedRoute permissions={['merchants.view']}><CommissionsScreen /></ProtectedRoute>} />

                  {/* Order Management - requires orders.view */}
                  <Route path="/rfqs" element={<ProtectedRoute permissions={['orders.view']}><RFQsScreen /></ProtectedRoute>} />
                  <Route path="/orders" element={<ProtectedRoute permissions={['orders.view']}><OrdersScreen /></ProtectedRoute>} />

                  {/* Payment Management - requires payments.view */}
                  <Route path="/payments" element={<ProtectedRoute permissions={['payments.view']}><PaymentsScreen /></ProtectedRoute>} />
                  <Route path="/settlements" element={<ProtectedRoute permissions={['payments.view']}><SettlementsScreen /></ProtectedRoute>} />
                  <Route path="/disputes" element={<ProtectedRoute permissions={['payments.view']}><DisputesScreen /></ProtectedRoute>} />

                  {/* Financial Tooling & Observability - requires financials.view */}
                  <Route path="/financials/overview" element={<ProtectedRoute permissions={['financials.view']}><FinancialsOverviewScreen /></ProtectedRoute>} />
                  <Route path="/financials/adjustments" element={<ProtectedRoute permissions={['financials.adjust']}><AdjustmentsScreen /></ProtectedRoute>} />
                  <Route path="/financials/recharges" element={<ProtectedRoute permissions={['financials.view']}><FinancialsOverviewScreen /></ProtectedRoute>} />
                  <Route path="/financials/wallets/:merchantId/ledger" element={<ProtectedRoute permissions={['financials.view_ledger']}><MerchantLedgerViewer /></ProtectedRoute>} />
                  <Route path="/financials/ledger" element={<ProtectedRoute permissions={['financials.view_ledger']}><MerchantLedgerViewer /></ProtectedRoute>} />

                  {/* Invoicing & Tax Compliance */}
                  <Route path="/marketplace-invoices" element={<ProtectedRoute permissions={['marketplace_invoices.view']}><MarketplaceInvoicesScreen /></ProtectedRoute>} />
                  <Route path="/subscription-invoices" element={<ProtectedRoute permissions={['subscription_invoices.view']}><SubscriptionInvoicesScreen /></ProtectedRoute>} />
                  <Route path="/subscriptions/dashboard" element={<ProtectedRoute permissions={['subscription_invoices.view']}><SubscriptionsDashboard /></ProtectedRoute>} />

                  {/* Notifications */}
                  <Route path="/notifications" element={<ProtectedRoute permissions={['notifications.view']}><NotificationsScreen /></ProtectedRoute>} />
                  <Route path="/notification-templates" element={<ProtectedRoute permissions={['notifications.manage']}><NotificationTemplatesScreen /></ProtectedRoute>} />

                  {/* Analytics - requires analytics.view */}
                  <Route path="/analytics" element={<ProtectedRoute permissions={['analytics.view']}><AnalyticsScreen /></ProtectedRoute>} />

                  {/* Promotions Management */}
                  <Route path="/promotions" element={<ProtectedRoute permissions={['products.view']}><PromotionsScreen /></ProtectedRoute>} />

                  {/* Roles - requires roles.view */}
                  <Route path="/roles" element={<ProtectedRoute permissions={['roles.view']}><RolesScreen /></ProtectedRoute>} />

                  {/* Profile & Settings - accessible to all authenticated admins */}
                  <Route path="/admins" element={<ProtectedRoute requireSuperAdmin><AdminsScreen /></ProtectedRoute>} /> {/* Super Admins Only basically */}
                  <Route path="/profile" element={<ProfileScreen />} />
                  <Route path="/settings" element={<ProtectedRoute permissions={['settings.manage']}><SettingsScreen /></ProtectedRoute>} />
                  <Route path="/audit-logs" element={<ProtectedRoute permissions={['audit.view']}><AuditLogsScreen /></ProtectedRoute>} />
                  <Route path="/units" element={<ProtectedRoute permissions={['products.view']}><UnitsScreen /></ProtectedRoute>} />

                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/reports/revenue" element={<PlaceholderScreen />} />
                  <Route path="/reports/wallets" element={<PlaceholderScreen />} />
                  <Route path="/reports/recharges" element={<PlaceholderScreen />} />
                  <Route path="/reports/ads" element={<PlaceholderScreen />} />
                  <Route path="*" element={<NotFoundScreen />} />
                </Route>
              </Route>

              {/* Fallback for outside routes */}
              <Route path="*" element={<NotFoundScreen />} />
            </Routes>
          </BrowserRouter>
        </Sentry.ErrorBoundary>
        </AntApp>
      </ConfigProvider>
    </QueryClientProvider>
  )
}

export default App
